/* Hallmark · component-scope redesign: auth card · genre: editorial · theme: host_ui.css (design.md tokens)
 * pre-emit critique: P5 H5 E5 S5 R5 V4
 */
import React from "react";
import { useContent } from "@thoughtbot/superglue";

import {
  Form,
  type FormProps,
  type RailsEmailFieldProps,
  type RailsPasswordFieldProps,
  type RailsSubmitButtonProps,
} from "@javascript/components/Inputs";
import { AuthCard } from "@javascript/components/AuthCard";
import { AuthConsentNotice } from "@javascript/components/AuthConsentNotice";
import { AuthShell } from "@javascript/components/AuthShell";
import { GoogleAuthButton } from "@javascript/components/GoogleAuthButton";
import { Button } from "@javascript/components/ui/button";
import { Field, FieldDescription, FieldError, FieldLabel } from "@javascript/components/ui/field";
import { Input } from "@javascript/components/ui/input";
import { TypographyH1, TypographyMuted } from "@javascript/components/ui/typography";

type SignUpContent = {
  signUpForm: FormProps<{
    email: RailsEmailFieldProps;
    password: RailsPasswordFieldProps;
    passwordConfirmation: RailsPasswordFieldProps;
    submit: RailsSubmitButtonProps;
  }>;
  errors: Record<string, string[]>;
  minimumPasswordLength?: number;
  signInPath: string;
  brandMotifPath: string;
  googleAuthPath?: string;
};

export default function SignUp() {
  const { signUpForm, errors, minimumPasswordLength, signInPath, brandMotifPath, googleAuthPath } =
    useContent<SignUpContent>();
  const { form, extras, inputs } = signUpForm;
  const emailErrors = (errors.email ?? []).map((message) => ({ message }));
  const passwordErrors = (errors.password ?? []).map((message) => ({ message }));
  const passwordConfirmationErrors = (errors.password_confirmation ?? []).map((message) => ({ message }));

  return (
    <AuthShell brandMotifPath={brandMotifPath}>
      <AuthCard>
        <div className="space-y-2">
          <TypographyH1 className="text-left">Create your account</TypographyH1>
          <TypographyMuted>Start creating your wedding invitation.</TypographyMuted>
        </div>

        {googleAuthPath && (
          <>
            <div className="mt-6">
              <GoogleAuthButton authPath={googleAuthPath} />
            </div>

            <div className="my-6 flex items-center gap-4">
              <div className="flex-1 border-t border-border"></div>
              <span className="text-sm text-muted-foreground">Or</span>
              <div className="flex-1 border-t border-border"></div>
            </div>
          </>
        )}

        {/* No data-sg-visit: sign-up changes currentUser identity, so the whole app needs a full reload to flush stale in-memory Superglue state. */}
        <Form {...form} extras={extras} className="mt-6 space-y-5">
          <Field data-invalid={emailErrors.length > 0}>
            <FieldLabel htmlFor={inputs.email.id}>Email</FieldLabel>
            <Input {...inputs.email} placeholder="you@example.com" aria-invalid={emailErrors.length > 0} />
            <FieldError errors={emailErrors} />
          </Field>
          <Field data-invalid={passwordErrors.length > 0}>
            <FieldLabel htmlFor={inputs.password.id}>Password</FieldLabel>
            <Input {...inputs.password} aria-invalid={passwordErrors.length > 0} />
            {minimumPasswordLength && (
              <FieldDescription>{minimumPasswordLength} characters minimum</FieldDescription>
            )}
            <FieldError errors={passwordErrors} />
          </Field>
          <Field data-invalid={passwordConfirmationErrors.length > 0}>
            <FieldLabel htmlFor={inputs.passwordConfirmation.id}>Password confirmation</FieldLabel>
            <Input {...inputs.passwordConfirmation} aria-invalid={passwordConfirmationErrors.length > 0} />
            <FieldError errors={passwordConfirmationErrors} />
          </Field>
          <Button type="submit" className="h-12 w-full rounded-full text-base font-semibold shadow-sm">
            {inputs.submit.text}
          </Button>
        </Form>
      </AuthCard>

      {/* Outside the card on purpose: the card is the choice between Google and
          email, and these speak about the account around that choice. */}
      <div className="mt-5">
        <p
          data-auth-signin-prompt
          className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm"
        >
          <span className="text-muted-foreground">Already have an account?</span>
          <a
            href={signInPath}
            className="inline-flex min-h-11 items-center gap-1.5 whitespace-nowrap font-semibold text-foreground underline-offset-4 transition-colors hover:text-primary hover:underline focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
          >
            Sign in
            <span aria-hidden className="text-primary">
              →
            </span>
          </a>
        </p>

        <div className="mt-4">
          <AuthConsentNotice />
        </div>
      </div>
    </AuthShell>
  );
}
